import type { Atom } from 'jotai';
import type { TaskModeT } from '../../../../../common/task-state/constants';
import type { AnnotationLabelT, AnnotationUnderUserReviewMapT } from '../../../../../common/types/types';
import type { RelationshipT } from '../../../../../modernization-common/relationships/types/relationship';
import type { VisibilityMode } from '../../../../../modernization-common/side-panel/atoms/expansion-atoms';
import { VISIBILITY_MODE } from '../../../../../modernization-common/side-panel/atoms/expansion-atoms';
export type { VisibilityMode };
export { VISIBILITY_MODE };
import type { ImageSettingsT } from '../../../../labelling/providers/stage-provider';
import type { AnnotationStoreT, BrushSettingsT, EditorModeT, FrameStateT, GridModeT, MetaDataT, SubToolT, ToolT } from '../../../../labelling/types/types';
import type { dispatchAtom } from '../../../atoms/label-store-dispatch/dispatch';

// Pass any Jotai atom (read-only or writable) → factory uses it directly (shared).
// Pass a plain value → factory creates a new owned atom (isolated per instance).
export type AtomOrValue<T> = T | Atom<T>;
interface StageFields {
  annotationStore: AnnotationStoreT;
  mlAnnotationFrames: Map<number, AnnotationStoreT>;
  consensusAnnotationFrames: Map<number, AnnotationStoreT>;
  imageUrl: string;
  frameCount: number;
  currentFrame: number;
  annotationUnderUserReview: AnnotationUnderUserReviewMapT;
  selectedAnnotationIds: string[];
  selectedTool: ToolT;
  multiSelectEnabled: boolean;
  gridMode: GridModeT;
  brushToolSettings: BrushSettingsT;
  snapEnabled: boolean;
  editorMode: EditorModeT;
  isLoading: boolean;
  fileType: string;
  metadata: MetaDataT;
  imageSettings: ImageSettingsT;
  taskMode: TaskModeT;
  isFocusModeEnabled: boolean;
  visibleRelationships: [string, RelationshipT][];
  relationshipEditorOpen?: boolean;
  selectedAnnotationLabel?: AnnotationLabelT | null;
  selectedSubTool?: SubToolT;
  focusedAnnotationId?: string | null;
  hoveredFrame?: number | null;
  copiedAnnotationId?: string | null;
  dataUrl?: string;
  visibilityMode?: VisibilityMode;
}
export type StageConfig = { [K in keyof StageFields]: AtomOrValue<StageFields[K]> } & {
  dispatchAtom: typeof dispatchAtom;
};
type ResolvedFields = { [K in keyof Required<StageFields> as `${K & string}Atom`]: Atom<StageFields[K]> };

// What <StageScope> provides to children via useStageAtoms().
// All fields are read-only atoms + derived atoms.
// dispatchAtom is the only writable entry — it is the sole write path.
export interface StageAtoms extends ResolvedFields {
  currentFrameDataAtom: Atom<FrameStateT>;
  totalFramesAtom: Atom<number>;
  editableAnnotationAtom: Atom<string | null>;
  isReviewFocusModeEnabledAtom: Atom<boolean>;
  currentFrameAnnotationUnderUserReviewAtom: Atom<AnnotationUnderUserReviewMapT>;
  annotationsUnderReviewBitmapsAtom: Atom<Record<string, ImageData>>;
  dispatchAtom: typeof dispatchAtom;
}